import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class BarcodeScannerService implements OnDestroy {
    private barcodeSubject = new Subject<string>();
    private buffer = '';
    private lastKeyTime = 0;
    // Number of consecutive sub-threshold keystrokes seen so far. A hardware
    // scanner produces a long run of these; a human never does.
    private fastRunLength = 0;
    private readonly SCAN_THRESHOLD = 50; // ms gap between keys that marks scanner-speed input
    // How many consecutive fast keystrokes must occur before we treat input as
    // a scan and start suppressing it from the focused field. Kept > 2 so that
    // even an unusually fast typist can never have their keystrokes swallowed.
    private readonly MIN_SCAN_LENGTH = 3;

    constructor() {
        window.addEventListener('keydown', this.handleKeyDown);
    }

    get barcodeScanned$(): Observable<string> {
        return this.barcodeSubject.asObservable();
    }

    private handleKeyDown = (event: KeyboardEvent) => {
        // Enter terminates a scanner burst. Only treat it as a scan (and
        // swallow it) when it immediately follows a confirmed fast run;
        // otherwise let it pass so normal form submission / Enter works.
        // Only a screen that consumes barcodeScanned$ (GRN) wants scans
        // intercepted. Everywhere else — the product form's EAN field, the
        // Bulk EAN grid, the barcode panel — a scan must land in the focused
        // input like ordinary typing, so with no subscriber this service
        // just watches and never swallows keystrokes.
        const intercept = this.barcodeSubject.observed;

        if (event.key === 'Enter') {
            const currentTime = Date.now();
            const isFast = currentTime - this.lastKeyTime <= this.SCAN_THRESHOLD;
            const wasScan =
                isFast && this.fastRunLength >= this.MIN_SCAN_LENGTH && this.buffer.length > 2;
            if (wasScan && intercept) {
                event.preventDefault();
                event.stopPropagation();
                this.barcodeSubject.next(this.buffer);
            }
            this.buffer = '';
            this.fastRunLength = 0;
            this.lastKeyTime = currentTime;
            return;
        }

        // Ignore non-printable keys (Shift, Ctrl, Alt, Arrows, Tab, etc.) so
        // they never distort the inter-character timing used to detect a
        // scanner. Crucially we do NOT touch lastKeyTime here.
        if (event.key.length !== 1) {
            return;
        }

        const currentTime = Date.now();
        const isFast = currentTime - this.lastKeyTime <= this.SCAN_THRESHOLD;
        this.lastKeyTime = currentTime;

        if (isFast) {
            this.buffer += event.key;
            this.fastRunLength++;
        } else {
            // A slow keystroke is human typing (or the first char of a scan):
            // start a fresh sequence. This guarantees the buffer is reset on
            // every human keystroke, so typing is never blocked.
            this.buffer = event.key;
            this.fastRunLength = 1;
        }

        // Only once we have a sustained fast run do we suppress the character
        // from a focused input. Human typing never reaches MIN_SCAN_LENGTH
        // consecutive sub-threshold gaps, so it is always allowed through.
        if (intercept && this.fastRunLength >= this.MIN_SCAN_LENGTH) {
            const target = event.target as HTMLElement | null;
            const inInput =
                !!target &&
                (target.tagName === 'INPUT' ||
                    target.tagName === 'TEXTAREA' ||
                    target.tagName === 'SELECT' ||
                    target.isContentEditable);
            if (inInput) {
                event.preventDefault();
                event.stopPropagation();
            }
        }
    };

    ngOnDestroy() {
        window.removeEventListener('keydown', this.handleKeyDown);
    }
}
